import React from "react";
import {
  ComposedChart,
  Bar,
  Cell,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  ReferenceLine,
} from "recharts";
import { simpleMovingAverage, bollingerBands, monthYearLabel, fullDateLabel, monthlyTicks } from "../../lib/indicators";

const BUY = "oklch(0.58 0.13 145)";
const SELL = "oklch(0.55 0.21 25)";
const MUTED = "oklch(0.552 0.016 285.938)";

/**
 * Renders a candlestick using a custom Bar shape.
 * Each datum has: open, high, low, close.
 */
function CandleShape(props) {
  const { x, y, width, height, payload, yScale } = props;
  if (!payload || payload.open == null || payload.close == null) return null;
  const isUp = payload.close >= payload.open;
  const color = isUp ? BUY : SELL;
  const cx = x + width / 2;

  // Use yScale if available; otherwise derive from y/height (Recharts passes high-low range)
  const high = payload.high;
  const low = payload.low;
  const openPx = yScale ? yScale(payload.open) : null;
  const closePx = yScale ? yScale(payload.close) : null;
  const highPx = yScale ? yScale(high) : y;
  const lowPx = yScale ? yScale(low) : y + height;

  if (yScale) {
    const bodyTop = Math.min(openPx, closePx);
    const bodyBottom = Math.max(openPx, closePx);
    const bodyHeight = Math.max(1, bodyBottom - bodyTop);
    const bodyWidth = Math.max(2, width * 0.7);
    return (
      <g>
        <line x1={cx} y1={highPx} x2={cx} y2={lowPx} stroke={color} strokeWidth={1} />
        <rect
          x={cx - bodyWidth / 2}
          y={bodyTop}
          width={bodyWidth}
          height={bodyHeight}
          fill={isUp ? color : color}
          opacity={isUp ? 0.85 : 1}
        />
      </g>
    );
  }
  // Fallback: simplified
  return (
    <g>
      <line x1={cx} y1={y} x2={cx} y2={y + height} stroke={color} strokeWidth={1} />
      <rect x={cx - width * 0.35} y={y + height * 0.35} width={width * 0.7} height={height * 0.3} fill={color} />
    </g>
  );
}

export default function CandlestickChart({ data, currency = "USD" }) {
  // Prepare with derived fields
  const closes = data.map((d) => Number(d.close));
  const ma20 = simpleMovingAverage(closes, 20);
  const ma50 = simpleMovingAverage(closes, 50);
  const { upper, lower } = bollingerBands(closes, 20, 2);

  const series = data.map((d, i) => ({
    raw_date: d.quote_date,
    open: Number(d.open),
    high: Number(d.high),
    low: Number(d.low),
    close: Number(d.close),
    volume: Number(d.volume),
    ma20: ma20[i] != null ? +ma20[i].toFixed(4) : null,
    ma50: ma50[i] != null ? +ma50[i].toFixed(4) : null,
    bb_upper: upper[i] != null ? +upper[i].toFixed(4) : null,
    bb_lower: lower[i] != null ? +lower[i].toFixed(4) : null,
    range: [Number(d.low), Number(d.high)],
  }));
  const ticks = monthlyTicks(series);

  // Compute price domain
  const lowsArr = series.map((s) => s.low).filter((v) => !isNaN(v));
  const highsArr = series.map((s) => s.high).filter((v) => !isNaN(v));
  const lowMin = Math.min(...lowsArr);
  const highMax = Math.max(...highsArr);
  const pad = (highMax - lowMin) * 0.08;
  const yDomain = [lowMin - pad, highMax + pad];

  return (
    <div className="space-y-2">
      <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
        Candlestick · MA20 · MA50 · Bollinger (20, 2σ)
      </div>
      <div className="h-[520px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.92 0.004 286.32)" vertical={false} />
            <XAxis dataKey="raw_date" ticks={ticks} tickFormatter={monthYearLabel} tick={{ fontSize: 10 }} stroke={MUTED} interval={0} />
            <YAxis
              domain={yDomain}
              tick={{ fontSize: 10 }}
              stroke={MUTED}
              tickFormatter={(v) => (currency === "BRL" ? `R$ ${v.toFixed(2)}` : `$${v.toFixed(2)}`)}
              width={75}
            />
            <Tooltip
              contentStyle={{
                background: "var(--popover)",
                border: "1px solid var(--border)",
                fontSize: 12,
              }}
              labelFormatter={fullDateLabel}
              formatter={(value, name) => {
                if (Array.isArray(value)) return [`${value[0].toFixed(2)} – ${value[1].toFixed(2)}`, "Faixa"];
                if (typeof value === "number") return [value.toFixed(4), name];
                return [value, name];
              }}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar
              dataKey="range"
              shape={(props) => <CandleShape {...props} yScale={props.yAxis?.scale} />}
              name="Candle"
              isAnimationActive={false}
            >
              {series.map((s, idx) => (
                <Cell key={idx} payload={s} />
              ))}
            </Bar>
            <Line type="monotone" dataKey="bb_upper" stroke={MUTED} strokeDasharray="4 4" strokeWidth={1} dot={false} name="BB Sup" isAnimationActive={false} />
            <Line type="monotone" dataKey="bb_lower" stroke={MUTED} strokeDasharray="4 4" strokeWidth={1} dot={false} name="BB Inf" isAnimationActive={false} />
            <Line type="monotone" dataKey="ma20" stroke="oklch(0.52 0.105 223.128)" strokeWidth={1.6} dot={false} name="MA20" isAnimationActive={false} />
            <Line type="monotone" dataKey="ma50" stroke="oklch(0.45 0.085 224.283)" strokeWidth={1.6} strokeDasharray="2 4" dot={false} name="MA50" isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
